import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import 'react-virtualized/styles.css';
import {
  Column,
  Table,
  AutoSizer,
  InfiniteLoader,
  defaultTableRowRenderer,
} from 'react-virtualized';
import { Loading } from 'components/Loading';
import StatusIcon from 'components/StatusIcon';

import cx from 'classnames';
import styles from './styles.module.scss';

const LoadingRow = ({ text, style, ...props }) => (
  <div style={style} className={styles.loadingRow} {...props}>
    <Loading size={20} /> {text}
  </div>
);

LoadingRow.propTypes = {
  style: PropTypes.object,
  text: PropTypes.string,
};

class UsersList extends Component {
  constructor(props) {
    super(props);
    this.isRowLoaded = this.isRowLoaded.bind(this);
    this.loadMoreRows = this.loadMoreRows.bind(this);
    this.rowCount = this.rowCount.bind(this);
    this.rowGetter = this.rowGetter.bind(this);
    this.rowRenderer = this.rowRenderer.bind(this);
    this.noRowsRenderer = this.noRowsRenderer.bind(this);
    this.headerRowRenderer = this.headerRowRenderer.bind(this);
    this.onRowClick = this.onRowClick.bind(this);
  }

  isRowLoaded({ index }) {
    const { users, isNextPageLoading, hasNextPage } = this.props;
    return index < users.length - 1 || isNextPageLoading || !hasNextPage;
  }

  loadMoreRows() {
    this.props.onLoadMoreRows();
  }

  rowGetter({ index }) {
    const { users } = this.props;
    return users[index];
  }

  rowCount() {
    const { hasNextPage, total } = this.props;

    if (hasNextPage) {
      return total + 1;
    }
    return total;
  }

  rowRenderer({ index, key, className, rowData, style, ...props }) {
    if (this.isRowLoaded({ index })) {
      return defaultTableRowRenderer({
        index,
        key,
        style,
        className,
        rowData,
        ...props,
      });
    }
    return <LoadingRow key={key} style={style} text="Loading..." />;
  }

  getRowClassName = ({ index }) => {
    const user = this.props.users[index];
    return cx(styles.row, {
      [styles.blocked]: user && !user.is_active,
    });
  };

  noRowsRenderer = (string) => () => {
    return <div className={styles.noResults}>{string}</div>;
  };

  headerRowRenderer({ columns, style, ...props }) {
    return (
      <div className={styles.header} role="row" style={style}>
        {columns}
      </div>
    );
  }

  onRowClick({ rowData }) {
    this.props.onRowClick(rowData.id);
  }

  statusRenderer({ rowData }) {
    return <StatusIcon isBlocked={() => !rowData.is_active} isActive={() => rowData.has_logged} />;
  }

  render() {
    const { threshold, users, intl } = this.props;
    const t = intl.messages.components.UsersList;

    return (
      <div className={styles.table}>
        <InfiniteLoader
          isRowLoaded={this.isRowLoaded}
          loadMoreRows={this.loadMoreRows}
          rowCount={this.rowCount()}
          threshold={threshold}
        >
          {({ onRowsRendered }) => (
            <AutoSizer>
              {({ width, height }) => (
                <Table
                  width={width}
                  height={height}
                  headerHeight={40}
                  rowHeight={48}
                  onRowsRendered={onRowsRendered}
                  rowClassName={this.getRowClassName}
                  rowCount={users.length}
                  rowGetter={this.rowGetter}
                  rowRenderer={this.rowRenderer}
                  headerRowRenderer={this.headerRowRenderer}
                  noRowsRenderer={this.noRowsRenderer(t.no_results)}
                  overscanRowCount={100}
                  onRowClick={this.onRowClick}
                >
                  <Column
                    dataKey="status"
                    width={26}
                    cellRenderer={this.statusRenderer}
                    className={styles.statusColumn}
                    minWidth={26}
                  />
                  <Column label={t.name_and_surname} dataKey="name" flexGrow={1} width={300} />
                  <Column label={t.role} dataKey="groupsString" flexGrow={1} width={300} />
                  <Column label={t.email} dataKey="email" width={300} />
                </Table>
              )}
            </AutoSizer>
          )}
        </InfiniteLoader>
      </div>
    );
  }
}

UsersList.propTypes = {
  users: PropTypes.arrayOf(PropTypes.object),
  total: PropTypes.number,
  threshold: PropTypes.number,
  hasNextPage: PropTypes.bool,
  isNextPageLoading: PropTypes.bool,
  onLoadMoreRows: PropTypes.func,
  onRowClick: PropTypes.func,
  intl: PropTypes.object.isRequired,
};

export default injectIntl(UsersList);
